import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";
import "../css/TableSelect.css";

import PropTypes from "prop-types";
import toast from "react-hot-toast";
import RestroOrderService from "../../../../../services/RestroOrder/RestroOrderService";

const TableSwitch = ({
  isOpen,
  toggle = () => {},
  selectedCourt,
  data = [],
  loading,
  mode = "switch-table", // "switch-table" | "merge-court" | "switch-item"
  selectedItems,
  orderNo,
  fetchOrder,
  orderId,
  handleRefresh,
  isRefreshing,
  fetchSection,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [tableName, setTableName] = useState(""); // Only for manual entry in switch-table (if needed)

  // Single selection for switch-table / switch-item
  const [tempSelectedTable, setTempSelectedTable] = useState(null);

  // Multiple selection for merge-court
  const [tempSelectedCourts, setTempSelectedCourts] = useState([]);
  console.log("🚀 ~ TableSwitch ~ tempSelectedCourts:", tempSelectedCourts);

  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  if (!isOpen) return null;

  // Filter tables based on search
  const filteredTables = data.filter((table) =>
    table.TableKey?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const getTableStatus = (table) => {
    switch (table?.QuickOrderStatus) {
      case 2:
        return "billPrinted";
      case 0:
        return "occupied";
      default:
        return "unknown";
    }
  };
  // Check if a table is selected (single or multiple)
  const hasSelection =
    mode === "merge-court"
      ? tempSelectedCourts.length > 0
      : tempSelectedTable !== null;

  const handleConfirm = async () => {
    try {
      if (mode === "switch-table") {
        if (!tableName.trim() && !tempSelectedTable) {
          toast.error("Please select or enter a table");
          return;
        }

        await RestroOrderService.shiftFoodCourt({
          username: "superuser",
          orderMasterID: selectedCourt?.OrderMasterID,
          newTableKey: tempSelectedTable?.TableKey || tableName,
        });
      } else if (mode === "merge-court") {
        if (tempSelectedCourts.length === 0) {
          toast.error("Please select at least one table to merge");
          return;
        }

        // Assuming you have a merge API — adjust as needed
        await RestroOrderService.mergeCourt({
          orderMasterID: selectedCourt?.OrderMasterID,
          tableData: tempSelectedCourts.map((t) => t.TableKey).join(","),
          username: "superuser",
        });
      } else if (mode === "switch-item") {
        if (!tempSelectedTable) {
          toast.error("Please select a target table");
          return;
        }

        const itemList = selectedItems.map((item) => ({
          ItemId: String(item?.productId),
          IsCombo: "false",
          ItemName: item?.productName,
          Quantity: item?.finalQuantity ?? 1,
        }));

        await RestroOrderService.shiftItem({
          orderMasterId: orderId,
          toTable: tempSelectedTable?.TableKey,
          shiftedBy: user?.Roles ?? "superuser",
          fromTable: selectedCourt?.TableKey,
          fromTableTitle: selectedCourt?.restrotableTitle,
          toTableTitle: tempSelectedTable?.restrotableTitle,
          orderNo,
          fromSplitNo: "1",
          toSplitNo: "1",
          itemList,
        });
      }

      toast.success(
        mode === "merge-court"
          ? "Tables merged successfully!"
          : "Action completed!"
      );
      fetchOrder();
      fetchSection();
      toggle();
    } catch (err) {
      console.error("Error in handleConfirm:", err);
      toast.error(err?.message || "Operation failed.");
    }
  };

  const renderTableVisual = (table, scale = 0.6) => {
    if (!table) return null;
    const status = getTableStatus(table);

    return (
      <div
        className={`table-card table-${status}`}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          pointerEvents: "none",
        }}
      >
        <div className="table-content ">
          <div className="table-name">{table.TableKey}</div>
          {table.OrderTime && (
            <div className="text-muted small">{table.OrderTime}</div>
          )}
        </div>
      </div>
    );
  };

  const renderSelectedTableInfo = () => {
    if (!selectedCourt && mode !== "merge-court") return null;

    return (
      <Card className="selected-table-info mb-3 border bg-light">
        <CardBody className="rounded-3">
          {/* Current Table */}
          {selectedCourt && (
            <div className="d-flex align-items-center gap-3 mb-4 ">
              <div className="selected-table-visual ">
                {renderTableVisual(selectedCourt, 0.7)}
              </div>
              <div>
                <h6 className="mb-1 fw-bold">
                  Current: {selectedCourt.TableKey}
                </h6>
                <small className="text-muted">
                  {selectedCourt.OrderTime || "Empty"}
                </small>
              </div>
            </div>
          )}

          <div className="text-center my-3">
            <i className="ri-arrow-down-line text-primary fs-4"></i>
            <div className="text-muted small">
              {mode === "merge-court"
                ? "Select tables to merge into"
                : "Select target table"}
            </div>
          </div>

          {/* Selected Target Tables */}
          {mode === "merge-court" ? (
            tempSelectedCourts.length > 0 ? (
              tempSelectedCourts.map((table) => (
                <div
                  key={table.TableKey}
                  className="d-flex align-items-center gap-3 mb-2 border-bottom pb-2"
                >
                  <div className="selected-table-visual">
                    {renderTableVisual(table, 0.6)}
                  </div>
                  <div>
                    <h6 className="mb-0 text-success">{table.TableKey}</h6>
                    <small className="text-muted">
                      {table.OrderTime || "Empty"}
                    </small>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted py-3">
                <i className="ri-hand-finger-line"></i> Tap tables below to
                select
              </div>
            )
          ) : tempSelectedTable ? (
            <div className="d-flex align-items-center gap-3">
              <div className="selected-table-visual">
                {renderTableVisual(tempSelectedTable, 0.7)}
              </div>
              <div>
                <h6 className="mb-1 text-success">
                  Target: {tempSelectedTable.TableKey}
                </h6>
                <small className="text-muted">
                  {tempSelectedTable.OrderTime || "Empty"}
                </small>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted py-3">
              <i className="ri-hand-finger-line"></i> Select a table below
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      backdropClassName="bg-dark bg-opacity-50"
      className="shadow-lg animate__animated animate__slideInRight bg-white rounded-start"
      id="offcanvasRight"
    >
      {/* OffcanvasHeader for mobile */}
      <OffcanvasHeader className="d-block d-sm-none">
        <div className="d-flex justify-content-between align-items-center w-100 rounded-top">
          <button className="btn btn-sm btn-light" onClick={toggle}>
            <i className="bx bx-chevron-left"></i>
          </button>
          <div className="d-flex align-items-center text-center justify-content-center w-100 gap-2">
            <i className="ri-stack-line"></i>
            <h5 className="mb-0">
              {mode === "switch-table" ? "Table Switch" : "Merge Court"}
            </h5>
          </div>
        </div>
      </OffcanvasHeader>

      <div
        className="position-absolute d-none d-sm-block"
        style={{ left: "-40px", top: "10px" }}
      >
        <button className="btn btn-sm btn-light" onClick={toggle}>
          <i className="ri-close-line"></i>
        </button>
      </div>

      {/* Desktop Header */}
      <div className="d-none d-sm-flex justify-content-start align-items-center p-3 shadow-sm rounded-top">
        <i className="ri-stack-line"></i>
        <h5 className="mb-0 ms-2">
          {mode === "switch-table" ? "Table Switch" : "Merge Court"}
        </h5>
      </div>

      <OffcanvasBody className="px-lg-4 px-2 py-3 bg-body-tertiary">
        <Row>
          <Col lg={4}>{renderSelectedTableInfo()}</Col>

          <Col lg={8}>
            {/* Manual Table Input (only for switch-table if no visual selection) */}
            {mode === "switch-table" && !tempSelectedTable && (
              <Card className="mb-3">
                <CardBody>
                  <Input
                    placeholder="Or type table name (e.g., T10)"
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                  />
                </CardBody>
              </Card>
            )}
            {mode === "merge-court" ? (
              <Card>
                <CardBody className="bg-light rounded-3">
                  {/* Search & Filters same as before */}
                  {/* ... */}

                  <div className="table-grid p-3">
                    {loading ? (
                      <div className="text-center my-5">
                        <div className="spinner-border text-primary" />
                      </div>
                    ) : filteredTables.length === 0 ? (
                      <div className="text-center my-5 text-muted">
                        <i className="ri-inbox-line display-5"></i>
                        <p>No tables found</p>
                      </div>
                    ) : (
                      filteredTables.map((table) => {
                        const status = getTableStatus(table);

                        const isCurrent =
                          selectedCourt?.TableKey === table.TableKey;
                        const isSelected =
                          mode === "merge-court"
                            ? tempSelectedCourts.some(
                                (t) => t.TableKey === table.TableKey
                              )
                            : tempSelectedTable?.TableKey === table.TableKey;

                        const isDisabled = isCurrent;

                        return (
                          <div
                            key={table.TableKey}
                            onClick={() => {
                              if (isDisabled) return;

                              if (mode === "merge-court") {
                                setTempSelectedCourts((prev) =>
                                  prev.some(
                                    (t) => t.TableKey === table.TableKey
                                  )
                                    ? prev.filter(
                                        (t) => t.TableKey !== table.TableKey
                                      )
                                    : [...prev, table]
                                );
                              } else {
                                setTempSelectedTable(
                                  tempSelectedTable?.TableKey === table.TableKey
                                    ? null
                                    : table
                                );
                              }
                            }}
                            className={`table-card table-${status} table-Seatcap-8 ${
                              isSelected ? "table-selected" : ""
                            } ${isCurrent ? "table-current" : ""} ${
                              isDisabled ? "table-disabled" : "cursor-pointer"
                            }`}
                          >
                            {isCurrent && (
                              <div className="table-current-indicator">
                                <i className="ri-home-line"></i> Current
                              </div>
                            )}
                            {renderTableVisual(table, 1)}
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardBody>
              </Card>
            ) : null}
          </Col>
        </Row>
      </OffcanvasBody>

      <ModalFooter className="bg-light border-top">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <div>
            {hasSelection && (
              <span>
                {mode === "merge-court"
                  ? `${tempSelectedCourts.length} table(s) selected`
                  : `Target: ${tempSelectedTable.TableKey || tableName}`}
              </span>
            )}
          </div>
          <Button
            color="success"
            onClick={handleConfirm}
            // disabled={!hasSelection}
          >
            {mode === "merge-court" ? "Merge Tables" : "Confirm"}
          </Button>
        </div>
      </ModalFooter>
    </Offcanvas>
  );
};

TableSwitch.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  selectedCourt: PropTypes.object,
  // setSelectedCourt: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.number,
    tables: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        tableName: PropTypes.string,
        Seatcap: PropTypes.number,
        tableStatus: PropTypes.string,
        section: PropTypes.object,
      })
    ),
  }),
  sectionData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  setSelectedSection: PropTypes.func,
  selectedSection: PropTypes.object,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(["switch-table", "switch-item"]),
};

export default TableSwitch;
